import React from 'react';
import { SearchBreadcrumb } from '../../../../../../components/molecules/SearchBreadcrumb';
import { ContentParagraph, ContentParagraphModifier } from '../../../../ContentParagraph';
import { ButtonMinorAction } from '../../../../../../components/molecules/Btn/ButtonWrapper';
import { ContentHeadline } from '../../../../ContentHeadline';
import { Icons } from '../../../../../../components/atoms/SVG';
import { getHexColor } from '../../../../../../types/ICategory';
import { IConnectedPage } from '../../../../../../types/api/connectedPages/IConnectedPage';
import { useSonTranslation } from '../../../../../../translation/useSonTranslation';
import { ConnectedPageListItemPersons } from '../ConnectedPageListItemPersons/ConnectedPageListItemPersons';
import { PageConnectionDetails } from '../../../../PageConnectionDetails/PageConnectionDetails';
import { LinkedTeaserImage } from '../../../../../../components/molecules/LinkedTeaserImageInLists';
import { useHtmlValidId } from '../../../../../../hooks/useHtmlValidId';
import { ConnectedPagesModeSearchPaths } from '../../../../../../types/api/connectedPages/ConnectedPagesMode';
import { TooltipPlacement } from '../../../../../../components/atoms/Tooltip';
import { useConnectedPagesExpandHandler } from '../../../useConnectedPagesExpandHandler/useConnectedPagesExpandHandler';
import { PageConnectionDetailsModifier } from '../../../../PageConnectionDetails/PageConnectionDetails';
import { ContentHeadlineModifier } from '../../../../ContentHeadline/ContentHeadline';

type ConnectedPagesListItemProps = {
  page: IConnectedPage;
  depth?: number;
};

export function ConnectedPageListItem({ page, depth = 0 }: ConnectedPagesListItemProps) {
  const { t } = useSonTranslation('ConnectedPageListItem');

  const { onExpandedButtonClick, isActive } = useConnectedPagesExpandHandler(page);

  const {
    pageLink,
    description = '',
    path = [],
    categories = [],
    contactPersons = [],
    otherPersons = [],
    childPages = [],
    roleNames,
    pageType,
  } = page;
  const hasChildren = childPages.length > 0;
  const expandButtonText = t(isActive ? 'closeFurtherLevels' : 'openFurtherLevels', {
    depth: depth ? depth + 2 : 2,
  });
  const hasContactPersons = contactPersons.length > 0;
  const hasOtherPersons = otherPersons.length > 0;

  const uId = useHtmlValidId();

  return (
    <li className="connected-page-list-item" key={pageLink.url}>
      <div className="connected-page-list-item__container" style={{ borderLeftColor: getHexColor(categories) }}>
        {page.teaserImage && (
          <div className="connected-page-list-item__image-container">
            <LinkedTeaserImage imageSrc={page.teaserImage.src} pageLink={page.pageLink.url} />
          </div>
        )}
        <article className="connected-page-list-item__article">
          <div className="connected-page-list-item__connected-page-container">
            <div className="connected-page-list-item__connected-page">
              <ContentHeadline
                url={`${pageLink.url}${hasChildren ? `?${ConnectedPagesModeSearchPaths.tree}` : ''}`}
                title={pageLink.name}
                headingId={`connected-page-item-headline-${uId}`}
                modifier={ContentHeadlineModifier.TOP_HEADLINE}
              />
              <PageConnectionDetails
                modifier={PageConnectionDetailsModifier.PADDING_BOTTOM}
                roles={roleNames}
                pageType={pageType}
                categories={categories}
              />
              <ContentParagraph modifier={ContentParagraphModifier.MARGIN_BOTTOM}>{description}</ContentParagraph>
              <SearchBreadcrumb breadcrumbs={path} />
            </div>
            {hasChildren && (
              <span className="connected-page-list-item__icon-container">
                <ButtonMinorAction
                  icon={isActive ? Icons.ARROW_UP : Icons.ARROW_DOWN}
                  text={expandButtonText}
                  onClick={onExpandedButtonClick}
                  ariaControls={`${pageLink.url}-content`}
                  ariaExpanded={isActive}
                  tooltipPlacement={TooltipPlacement.TOP}
                />
              </span>
            )}
          </div>
          {(hasContactPersons || hasOtherPersons) && (
            <div className="connected-page-list-item__person-container">
              {hasContactPersons && (
                <ConnectedPageListItemPersons persons={contactPersons} headline={t('headlineContactPersons')} />
              )}
              {hasOtherPersons && (
                <ConnectedPageListItemPersons persons={otherPersons} headline={t('headlineAllPersons')} />
              )}
            </div>
          )}
        </article>
      </div>
      {childPages.length > 0 && (
        <div id={`${pageLink.url}-content`} className="connected-page-list-item__content" aria-hidden={!isActive}>
          {isActive && (
            <ul className="connected-page-list-item__sub-list">
              {childPages.map(childPage => (
                <ConnectedPageListItem key={`pageLink-${childPage.pageLink.url}`} page={childPage} depth={depth + 1} />
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}
